/**
 * Money, with the basis always attached.
 *
 * §6.11: **no chart or total may sum across `price_basis` without either
 * converting to the net basis or stating the mix.** The same rule applies to a
 * single cell: net and customer are different numbers, and a figure with no
 * basis beside it is a figure the reader will assume is whichever one suits
 * them.
 *
 * `price_basis` is an opportunity-level fact (§5.3) — a deal is either direct
 * or through a channel, never both — so one indicator per row is enough.
 */

import { Badge } from '@/components/ui/badge';
import { formatMoney, formatMoneyCompact, type MoneyLike } from '@/lib/format';
import { cn } from '@/lib/utils';

export type PriceBasis = 'menlo_net' | 'distributor_resale';

export const BASIS_LABEL: Record<PriceBasis, string> = {
  menlo_net: 'Menlo net',
  distributor_resale: 'Distributor resale',
};

/** A short tag for a dense table cell. */
export function PriceBasisTag({ basis }: { basis: string | null | undefined }): React.ReactElement {
  const resale = basis === 'distributor_resale';
  return (
    <Badge
      variant={resale ? 'info' : 'outline'}
      title={BASIS_LABEL[(basis as PriceBasis) ?? 'menlo_net']}
    >
      {resale ? 'resale' : 'direct'}
    </Badge>
  );
}

export function MoneyCell({
  value,
  compact = false,
  className,
}: {
  value: MoneyLike;
  compact?: boolean;
  className?: string;
}): React.ReactElement {
  return (
    <span className={cn('tabular-nums', className)}>
      {compact ? formatMoneyCompact(value) : formatMoney(value)}
    </span>
  );
}

/**
 * The detail rail's revenue block. **Both bases when the deal is on the
 * distributor-resale basis** (Phase 4), with the rep firm's `net_factor` named
 * — because "15%" read as a discount rather than a markup is a 2.3% error
 * across the whole funnel, and the factor is the only unambiguous form of it.
 */
export function DualBasisRevenue({
  customer,
  net,
  basis,
  netFactor,
  repFirmName,
}: {
  customer: MoneyLike;
  net: MoneyLike;
  basis: string | null | undefined;
  netFactor?: string | null | undefined;
  repFirmName?: string | null | undefined;
}): React.ReactElement {
  if (basis !== 'distributor_resale') {
    return (
      <div>
        <p className="text-xl font-semibold tabular-nums">{formatMoney(net)}</p>
        <p className="text-xs text-muted-foreground">
          Annual revenue — direct, so customer and Menlo net are the same number.
        </p>
      </div>
    );
  }

  const factor = netFactor ?? null;
  const markup = factor === null ? null : (1 / Number(factor) - 1) * 100;

  return (
    <div className="space-y-2">
      <div>
        <p className="text-xl font-semibold tabular-nums">{formatMoney(net)}</p>
        <p className="text-xs text-muted-foreground">
          Annual revenue, Menlo net — what Menlo books.
        </p>
      </div>
      <div>
        <p className="text-sm tabular-nums">{formatMoney(customer)}</p>
        <p className="text-xs text-muted-foreground">
          Customer resale — what the end customer pays.
        </p>
      </div>
      {factor !== null && (
        <p className="rounded bg-muted/60 p-2 text-[11px] text-muted-foreground">
          {repFirmName ?? 'The rep firm'} marks Menlo&rsquo;s price up by{' '}
          {markup === null ? '—' : markup.toFixed(1)}%, so Menlo books{' '}
          {(Number(factor) * 100).toFixed(1)}% of resale (net factor {factor}).
        </p>
      )}
    </div>
  );
}
